import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function relativeTime(iso) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (Number.isNaN(mins)) return "";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

async function fetchNotificationsInto(setData) {
  try {
    const { data } = await api.get("/notifications");
    setData(data);
  } catch {
    // Silent — the bell simply keeps its previous state on a failed poll.
  }
}

export function NotificationsBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const helpers = useRef({ fetchNotificationsInto });
  const load = useCallback(() => helpers.current.fetchNotificationsInto(setData), [helpers, setData]);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 60000);
    return () => clearInterval(t);
  }, [load]);

  const open = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    load();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button className="relative rounded-md p-2 text-[#002248] hover:bg-[#F4F4F5]"
          aria-label="Notifications" title="Notifications" data-testid="header-notifications">
          <Bell size={20} />
          {data.unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#DC2626] px-1 text-[10px] font-bold text-white"
              data-testid="notifications-unread-badge">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-md" data-testid="notifications-menu">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="text-sm font-semibold text-[#09090B]">Notifications</span>
          {data.unread > 0 && (
            <button type="button" onClick={markAll}
              className="text-xs font-semibold text-[#2E86D6] hover:underline"
              data-testid="notifications-mark-all-read">
              Mark all read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data.items.length === 0 ? (
          <p className="px-3 py-4 text-center text-xs text-[#6D6D70]" data-testid="notifications-empty">
            No notifications yet.
          </p>
        ) : (
          data.items.slice(0, 10).map((n) => (
            <DropdownMenuItem key={n.id} onClick={() => open(n)}
              className="cursor-pointer items-start gap-2 py-2.5"
              data-testid={`notification-item-${n.id}`}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-[#E4E4E7]" : "bg-[#DC2626]"}`} />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-[#09090B]">{n.title}</span>
                <span className="block text-xs leading-snug text-[#52525B]">{n.message}</span>
                <span className="mt-0.5 block text-[10px] text-[#9CA3AF]">{relativeTime(n.created_at)}</span>
              </span>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
